import { Dimensioning } from "../core/dimensioning";
import { EVENT_CORNER_2D_CLICKED, EVENT_NOTHING_2D_SELECTED, EVENT_WALL_2D_CLICKED, EVENT_ROOM_2D_CLICKED, EVENT_DOOR_2D_CLICKED, EVENT_FLOOR_ITEM_2D_CLICKED } from "../core/events";
import { InWallFloorItem } from "../items/in_wall_floor_item";
import { Utils } from '../core/utils.js';
import { Vector2 } from "three/build/three.module";
import { utils } from "pixi.js";
import { config } from "../core/configuration";

export class FloorPlannerHelper {
    constructor(floorplan, floorplanner) {
        this.__floorplan = floorplan;
        this.__floorplanner = floorplanner;

        this.__wallLength = 0;//the length of wall in cm of the selected wall
        this.__wallThickness = Dimensioning.cmToMeasureRaw(config.wallThickness);
        this.__cornerElevation = Dimensioning.cmToMeasureRaw(250);
        this.__roomName = 'A New Room';

        /**
         * Store a reference to the model entities
         */
        this.__selectedWall = null;
        this.__selectedCorner = null;
        this.__selectedRoom = null;
        this.__selectedDoor = null;
        this.__selectedFloorItem = null;

        /**
         * Store a reference to the viewer3d visual entities
         */
        this.__selectedWallEntity = null;
        this.__selectedCornerEntity = null;
        this.__selectedRoomEntity = null;
        this.__selectedDoorEntity = null;
        this.__selectedFloorItemEntity = null;

        this.__nothingSelectedEvent = this.__resetSelections.bind(this);
        this.__cornerSelectedEvent = this.__cornerSelected.bind(this);
        this.__wallSelectedEvent = this.__wallSelected.bind(this);
        this.__roomSelectedEvent = this.__roomSelected.bind(this);
        this.__doorSelectedEvent = this.__doorSelected.bind(this);
        this.__floorItemSelectedEvent = this.__floorItemSelected.bind(this);

        this.__floorplanner.addFloorplanListener(EVENT_NOTHING_2D_SELECTED, this.__nothingSelectedEvent);
        this.__floorplanner.addFloorplanListener(EVENT_CORNER_2D_CLICKED, this.__cornerSelectedEvent);
        this.__floorplanner.addFloorplanListener(EVENT_WALL_2D_CLICKED, this.__wallSelectedEvent);
        this.__floorplanner.addFloorplanListener(EVENT_ROOM_2D_CLICKED, this.__roomSelectedEvent);
        // added listener for a door clicked event
        this.__floorplanner.addFloorplanListener(EVENT_DOOR_2D_CLICKED, this.__doorSelectedEvent);
        //added listenre for floor item clicked event
        this.__floorplanner.addFloorplanListener(EVENT_FLOOR_ITEM_2D_CLICKED, this.__floorItemSelectedEvent);
        
        //viewer 3d
        this.viewer3D = null;
        this.viewer2D = null;
    }

    __resetSelections() {
        this.__selectedCorner = null;
        this.__selectedWall = null;
        this.__selectedRoom = null;
        this.__selectedCornerEntity = null;
        this.__selectedWallEntity = null;
        this.__selectedRoomEntity = null;
        this.__selectedDoor = null;
        this.__selectedDoorEntity =null;
        this.__selectedFloorItem = null;
        this.__selectedFloorItemEntity = null;
    }

    __cornerSelected(evt) {
        this.__resetSelections();
        this.__selectedCorner = evt.item;
        this.__selectedCornerEntity = evt.entity;
        this.__cornerElevation = Dimensioning.cmToMeasureRaw(this.__selectedCorner.elevation);
        this.viewer3D = evt.viewer3D;
        this.viewer2D = evt.viewer2D;
    }

    __wallSelected(evt) {
        //get the distance in cm
        let distance = Utils.distance(evt.item.start, evt.item.end)
        this.__resetSelections();
        this.__selectedWall = evt.item;
        this.__selectedWallEntity = evt.entity;
        this.__wallLength = distance;
        this.__wallThickness = Dimensioning.cmToMeasureRaw(evt.item.thickness);
        this.viewer3D = evt.viewer3D;
        this.viewer2D = evt.viewer2D;
    }

    __roomSelected(evt) {
        this.__resetSelections();
        this.__selectedRoom = evt.item;
        this.__selectedRoomEntity = evt.entity;
        this.__roomName = evt.item.name;
    }

    // set the clicked door as the current selcted door
    __doorSelected(evt){
        this.__resetSelections();
        this.__selectedDoor = evt.item;
        this.__selectedDoorEntity = evt.entity;
        this.viewer3D = evt.viewer3D;
        this.viewer2D = evt.viewer2D;
    }

    __floorItemSelected(evt){
        this.__resetSelections();
        this.__selectedFloorItem = evt.item;
        this.__selectedFloorItemEntity = evt.entity;
        this.viewer3D = evt.viewer3D;
        this.viewer2D = evt.viewer2D;
    }

    __nothingSelected() {
        this.__resetSelections();
    }

    //This function is just an helper for deleteCurrentItem() to reduce code duplication
    helperDeleteCurrentItem(item) {
        this.viewer3D.__physicalRoomItems.forEach(physicalItem => {
            if(physicalItem.__itemModel.__id === item.__id){
                this.viewer3D.deleteItem(physicalItem, false);
            }
        });
    }

    //delete an item or wall or corner (called from the 2D viewer but also affect the 3d viewer)
    deleteCurrentItem() {
        if (this.__selectedWall) {
            this.__selectedWall.__inWallItems.forEach( (item) => {//need to remove every item in the wall
                this.helperDeleteCurrentItem(item)
            })
            this.__selectedWall.remove();
            this.__resetSelections();
        }
        if (this.__selectedCorner) {
            //remove every item for every wall attached to the selected corner
            this.__selectedCorner.wallStarts.forEach( wall => {
                wall.__inWallItems.forEach( (item) => {//need to remove every item in the wall
                    this.helperDeleteCurrentItem(item)
                })
            })
            this.__selectedCorner.wallEnds.forEach( wall => {
                wall.__inWallItems.forEach( (item) => {//need to remove every item in the wall
                    this.helperDeleteCurrentItem(item)
                })
            })
            this.__selectedCorner.remove();
            this.__resetSelections();
        }
        if(this.__selectedDoor){
            // find the physical 3D item corresponding to the selected door
            let toDelete;
            this.viewer3D.__physicalRoomItems.forEach(element => {
                if(element.__itemModel.__id === this.__selectedDoor.__id){
                    toDelete = element;
                }
            });
            this.viewer3D.deleteItem(toDelete);
            //we also need to redraw the scene
            this.viewer2D.__redrawFloorplan();
        }
        if(this.__selectedFloorItem){
            let toDelete;
            this.viewer3D.__physicalRoomItems.forEach(element => {
                if(element.__itemModel.__id === this.__selectedFloorItem.__id){
                    toDelete = element;
                }
            });
            this.viewer3D.deleteItem(toDelete, false);//false since there are no wall
            //we also need to redraw the scene
            this.viewer2D.__redrawFloorplan();
        }
    }

    set wallLength(value) {// setter for the length of the selected wall
        if (this.__selectedWall) {
            this.__wallLength = value;
        }
    }

    get wallLength() {
        return this.__wallLength;
    }

    set wallThickness(value) {
        if (this.__selectedWall) {
            let cms = Dimensioning.cmFromMeasureRaw(value);
            this.__selectedWall.thickness = cms;
            this.__wallThickness = value;
        }
    }
    get wallThickness() {
        return Dimensioning.cmToMeasureRaw(this.__wallThickness);
    }

    set cornerElevation(value) {
        if (this.__selectedCorner) {
            let cms = Dimensioning.cmFromMeasureRaw(value);
            this.__selectedCorner.elevation = cms;
            this.__cornerElevation = value;
        }
    }
    get cornerElevation() {
        return Dimensioning.cmToMeasureRaw(this.__cornerElevation);
    }

    set roomName(value) {
        if (this.__selectedRoom) {
            this.__selectedRoom.name = value;
            this.__roomName = value;
        }
    }
    get roomName() {
        return this.__roomName;
    }
}